import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ProductCard } from '../../components/ProductCard/ProductCard';
import { NavigationTabs } from '../../components/NavigationTabs/NavigationTabs';
import { useProducts } from '../../hooks/useProducts';
import { Product } from '../../types/product';
import { toggleFavorite } from '../../api/productApi';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import styles from './Home.module.scss';

export const Home: React.FC = () => {
    const { products, loading, error } = useProducts();
    const [activeTab, setActiveTab] = useState<'products' | 'favorites' | 'account'>('products');
    const [localProducts, setLocalProducts] = useState<Product[]>([]);
    const [, setUpdatingFavoriteId] = useState<string | null>(null);

    useEffect(() => {
        setLocalProducts(products);
    }, [products]);

    const filteredProducts = useMemo(() => {
        return activeTab === 'favorites'
            ? localProducts.filter(p => p.isFavorite)
            : localProducts;
    }, [localProducts, activeTab]);

    const handleToggleFavorite = useCallback(async (productId: string) => {
        try {
            setUpdatingFavoriteId(productId);
            await toggleFavorite(productId);
            setLocalProducts(prev =>
                prev.map(p =>
                    p.id === productId ? { ...p, isFavorite: !p.isFavorite } : p
                )
            );
        } catch (e) {
            console.error('Ошибка при обновлении избранного:', e);
            alert('Не удалось изменить избранное. Попробуйте позже.');
        } finally {
            setUpdatingFavoriteId(null);
        }
    }, []);

    return (
        <div className={styles.homePage}>
            <div className={styles.content}>
                {loading && (
                    <div className={styles.loadingWrapper}>
                        <LoadingIndicator message="Загрузка товаров..." />
                    </div>
                )}
                {error && <div className={styles.error}>{error}</div>}
                {!loading && !error && filteredProducts.length > 0 && (
                    <div className={styles.productsGrid}>
                        {filteredProducts.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                className={styles.productCard}
                                onToggleFavorite={handleToggleFavorite}
                            />
                        ))}
                    </div>
                )}
                {!loading && !error && filteredProducts.length === 0 && activeTab === 'favorites' && (
                    <div className={styles.emptyState}>
                        В избранном пока нет товаров
                    </div>
                )}
            </div>
            <div className={styles.footer}>
                <NavigationTabs activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
        </div>
    );
};